// Proactive iOS permission prompts.
//
// The TestFlight build silently swallowed every permission ask because the
// app never tapped the right Capacitor API on the right user gesture. iOS
// only shows the system permission sheet on the first call to the API that
// needs it — if that call is buried behind a try/catch the trainer never
// sees the prompt.
//
// We split prompts into "boot" (push / local notifications, requested once
// per install on first Home entry) and "on demand" (photo library, when the
// user actually taps an import button). Broadcast (screen recording) is
// handled by the iOS broadcast picker itself when Live Scan is tapped.
import { Capacitor } from "@capacitor/core";
import { requestNotificationPermission } from "./notifications";
import { claimFirstRunPrompt, markFirstRunPromptDone } from "./firstRunPrompts";

const PRIME_KEY = "nx_perm_prime_v1";

function isNative(): boolean {
  try {
    return typeof Capacitor !== "undefined" && Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

async function alreadyPrimed(): Promise<boolean> {
  try {
    const { Preferences } = await import("@capacitor/preferences" as any);
    const res = await Preferences.get({ key: PRIME_KEY });
    return typeof res?.value === "string" && res.value.length > 0;
  } catch {
    try {
      return !!localStorage.getItem(PRIME_KEY);
    } catch {
      return false;
    }
  }
}

async function markPrimed(): Promise<void> {
  const stamp = String(Date.now());
  try {
    const { Preferences } = await import("@capacitor/preferences" as any);
    await Preferences.set({ key: PRIME_KEY, value: stamp });
  } catch {
    try {
      localStorage.setItem(PRIME_KEY, stamp);
    } catch {
      /* ignore */
    }
  }
}

// Called from the Profile (home) page once the schedule in lib/firstRunPrompts
// gives the notification sheet its turn. We only fire the push notifications
// ask; the photo library prompt is deferred to the moment the user taps an
// import action (Apple HIG: ask in context).
export async function primeBootPermissions(): Promise<void> {
  if (!isNative()) return;
  if (await alreadyPrimed()) {
    // Asked before the schedule existed: tell it, so the turn passes on.
    markFirstRunPromptDone("notifications");
    return;
  }
  // The sheet is about to appear: this session's one ask.
  claimFirstRunPrompt("notifications");
  let answer: string = "unknown";
  try {
    answer = await requestNotificationPermission();
  } catch {
    /* swallowed: the trainer gets another chance on the next broadcast */
  }
  // Only a real answer ends the boot ask (2026-09-30): a plugin hiccup or an
  // ask that never resolved used to mark it done, and the trainer was never
  // asked again.
  if (answer === "granted" || answer === "denied") {
    await markPrimed();
    markFirstRunPromptDone("notifications");
  }
}

